import axios, { AxiosResponse } from 'axios';
import { setupAuthInterceptor, retryWithBackoff } from '../common';
import { NAMESPACES } from '../configuration';
import { clearScope } from '../cache/cachePublicMethods';
import { isFeatureRolloutLocal } from '../../configuration/directMenuRollout';
import { uuidv4 } from '../../utils/functions';
const BASE_URL = NAMESPACES.pedidos_api.pedidos;
let pendingStockModification: { fingerprint: string; key: string } | null = null;

// Create axios instance with auth interceptor (fixes token race condition)
const api = setupAuthInterceptor(axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
}));


// Function to fetch all salones
export const getAllPedidos = async (): Promise<AxiosResponse<any>> => {
  return await retryWithBackoff(() =>
    api.get('/GetAllPedidos')
  );
};


// CreatePedidoV2

export const createPedidoV2 = async (pedidoData: any, idempotencyKey?: string) => {
  const res = await api.post('/CreatePedidoV2', pedidoData,
    idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {});
  // Invalidate related cached data: namespace 'management_api', group 'salones'
  await clearScope({ namespace: 'management_api', group: 'salones' });
  return res;
};

/** Server-owned policy; local IDs from browser storage never authorize stock writes. */
export const getStrictStockPolicy = (): Promise<AxiosResponse<{ local_id: number; strict_stock: boolean }>> =>
  api.get('/StrictStockPolicy', { timeout: 10000 });

// /GetActivePedidosByMesaId

export const getActivePedidosByMesaId = async (mesaId: number): Promise<AxiosResponse<any>> => {
  return await retryWithBackoff(() =>
    api.get(`/GetActivePedidosByMesaId/${mesaId}`)
  );
};


export const getActivePedidosByPedidoGroupId = async (pedidoGroupId: number): Promise<AxiosResponse<any>> => {
  return await retryWithBackoff(() =>
    api.get(`/GetActivePedidosByPedidoGroupId/${pedidoGroupId}`)
  );
};


// /GetActiveDeliveryPedidosByLocalId/<int:local_id>

export const getActiveDeliveryPedidosByLocalId = async (localId: number): Promise<AxiosResponse<any>> => {
  return await retryWithBackoff(() =>
    api.get(`/GetActiveDeliveryPedidosByLocalId/${localId}`)
  );
};

// /GetActiveCajaPedidosByLocalId/<int:local_id>

export const getActiveCajaPedidosByLocalId = async (localId: number): Promise<AxiosResponse<any>> => {
  return await retryWithBackoff(() =>
    api.get(`/GetActiveCajaPedidosByLocalId/${localId}`)
  );
};

// /GetAllPedidosByLocalId/<int:local_id>

export const getAllPedidosByLocalId = async (localId: number): Promise<AxiosResponse<any>> => {
  return await retryWithBackoff(() =>
    api.get(`/GetAllPedidosByLocalId/${localId}`)
  );
};

// /GetAllSessionPedidosByLocalId/<int:local_id>

export const getAllSessionPedidosByLocalId = async (localId: number): Promise<AxiosResponse<any>> => {
  return await retryWithBackoff(() =>
    api.get(`/GetAllSessionPedidosByLocalId/${localId}`)
  );
};



// /MarkPedidoAsReadyById/<int:pedido_id>

export const markPedidoAsReadyById = async (pedidoId: number) => {
  return await api.put(`/MarkPedidoAsReadyById/${pedidoId}`, {
  });
};

// /MarkPedidoAsDeliveredById/<int:pedido_id>

export const markPedidoAsDeliveredById = async (pedidoId: number) => {
  return await api.put(`/MarkPedidoAsDeliveredById/${pedidoId}`, {
  });
};

// /MarkPedidoAsPrintedById/<int:pedido_id>

export const markPedidoAsPrintedById = async (pedidoId: number) => {
  return await api.put(`/MarkPedidoAsPrintedById/${pedidoId}`, {
  });
};

// /MarkPedidoAsCancelledById/<int:pedido_id>

export const markPedidoAsCancelledById = async (pedidoId: number, print?: boolean) => {
  return await api.put(`/MarkPedidoAsCancelledById/${pedidoId}`, {
    print: print
  });
};

//UpdatePedidoSelectionsV2 that receives deleted_selections, modified_selections, and owner_changes

export const updatePedidoSelectionsV2 = async ( deleted_selections: any, 
  modified_selections: any, 
  owner_changes: any , 
  splitted_selections: any[] = [],
  comment?:string,
  modification_reason?:number,
  print?: boolean) => {
  const payload = {"deleted_selections": deleted_selections,
    "modified_selections": modified_selections, 
    "owner_changes": owner_changes,
    "splitted_selections": splitted_selections,
    "comment": comment,
    "modification_reason": modification_reason,
    "print": print
  };
  let localId: number | null = null;
  let actorId: number | null = null;
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    localId = user?.local_id;
    actorId = user?.user_id;
  } catch { /* Ordinary behavior is unchanged when rollout eligibility is absent. */ }
  let strict = false;
  if (localId && isFeatureRolloutLocal(localId)) {
    const policy = await getStrictStockPolicy();
    if (policy.data.local_id !== localId || typeof policy.data.strict_stock !== 'boolean')
      throw new Error('No se pudo verificar el control de stock');
    strict = policy.data.strict_stock;
  }
  if (!strict) return await api.put('/UpdatePedidoSelectionsV2', payload, {});
  const fingerprint = JSON.stringify({ localId, actorId, payload });
  if (pendingStockModification?.fingerprint !== fingerprint)
    pendingStockModification = { fingerprint, key: uuidv4() };
  const response = await api.put('/UpdatePedidoSelectionsV2', payload, {
    headers: { 'Idempotency-Key': pendingStockModification.key },
  });
  if (response.data?.strict_stock !== true)
    throw new Error('No se pudo confirmar la modificación');
  pendingStockModification = null;
  return response;
};

//Endpoint de mover mesa
export const moveTable = async (mesa_id: number, new_mesa_id: number) => {
  return await api.put(`/MoveTable/${mesa_id}/${new_mesa_id}`, {
  });
};


export const moveSelection = async (selection_id: number, pedido_id: number, mesa_id: number) => {
  return await api.put(`/MoveSelection`, {
    "selection_id": selection_id,
    "pedido_id": pedido_id,
    "mesa_id": mesa_id
  });
};

/**
 * 
move_multiple_selections_parser = reqparse.RequestParser()
move_multiple_selections_parser.add_argument('selections_to_move', type=dict, required=True, location='json', 
                                           help='Dictionary mapping pedido_id to list of selection_ids to move')
move_multiple_selections_parser.add_argument('target_mesa_id', type=int, required=True, help='ID of the target mesa')

@ns.route('/MoveMultipleSelections')
class MoveMultipleSelections(Resource):
    @ns.expect(move_multiple_selections_parser)
    @jwt_required()
    def put(self):
        """
        Move multiple selections from different pedidos to a target mesa.
        
        Request body example:
        {
            "selections_to_move": {
                "123": [1, 2, 3],     // Move selections 1,2,3 from pedido 123
                "456": [4, 5]         // Move selections 4,5 from pedido 456
            },
            "target_mesa_id": 789
        }
        """
        args = move_multiple_selections_parser.parse_args()
        helper = PedidoHelperV2()
        
        # Convert string keys to integers for pedido_ids
        selections_to_move = {}
        for pedido_id_str, selection_ids in args['selections_to_move'].items():
            try:
                pedido_id = int(pedido_id_str)
                selections_to_move[pedido_id] = selection_ids
            except ValueError:
                return {'message': f'Invalid pedido_id: {pedido_id_str}'}, 400
        
        _, data, status = helper.mesasController.moveMultipleSelections(selections_to_move, args['target_mesa_id'])
        return data, status
 */

export const moveMultipleSelections = async ( selections_to_move: Record<number, string[]>, target_mesa_id: number) => {
  return await api.put(`/MoveMultipleSelections`, {
    "selections_to_move": selections_to_move,
    "target_mesa_id": target_mesa_id
  });
}

//Endpoint de mover mesa
export const resetTable = async (mesa_id: number, comment?:string, print?: boolean) => {
  return await api.put(`/ResetTable/${mesa_id}`, {comment: comment, print: print
  });
};

export const resetPedidoGroup = async (pedido_group_id: number, comment?:string, print?: boolean) => {
  return await api.put(`/ResetGroup/${pedido_group_id}`, {comment: comment, print: print
  });
};

// Endpoint para cancelar los pedidos seleccionados
export const cancelSelectedPedidos = async (pedidosToCancel: number[], print?: boolean) => {
  return await api.put('/CancelSelectedPedidos', {
    pedidos_to_cancel: pedidosToCancel,
    print: print
  });
};


// Obtener pedidos por local_id y rango de fechas (GET)
export const getAllPedidosByLocalIdAndRangeDate = async (
  localId: number,
  startDate: string,
  endDate: string
): Promise<AxiosResponse<any>> => {
  return await retryWithBackoff(() =>
    api.get(`/GetAllPedidosByLocalIdAndRangeDate/${localId}/${startDate}/${endDate}`)
  );
};

/**
 * 
# UpdatePedidoCommentByPedidoId
update_comment_parser = reqparse.RequestParser()
update_comment_parser.add_argument('new_comment', type=str, location='json', required=True, help='New comment for the pedido')

@ns.route('/UpdatePedidoCommentByPedidoId/<int:pedido_id>')
@ns.param('pedido_id', 'The pedido identifier')
class UpdatePedidoCommentByPedidoId(Resource):
    @ns.expect(update_comment_parser)
    @jwt_required()
    def put(self, pedido_id):
        """Update the comment of a pedido by its ID"""
        args = update_comment_parser.parse_args()
        new_comment = args.get('new_comment')
        
        pedido = Pedido.query.get(pedido_id)
        if not pedido:
            return {'message': 'Pedido no encontrado o no existe'}, 404
        
        # Update the comment
        pedido.comment = new_comment
        db.session.commit()
        
        return {
            'message': 'Comentario del pedido actualizado exitosamente',
            'pedido_id': pedido_id,
            'new_comment': new_comment
        }, 200
 */

export const updatePedidoCommentByPedidoId = async (pedidoId: number, newComment: string) => {
  return await api.put(`/UpdatePedidoCommentByPedidoId/${pedidoId}`, {
    new_comment: newComment
  });
};
